/**
 * MostLovedPhraseCard
 *
 * Library/Home widget surfacing the single phrase that's been favorited
 * the most in the last 7 days. Uses `/api/phrases/most-loved-week`
 * (which aggregates `phrase_saved` events from `user_activities`).
 *
 * Auto-hides when nothing's been saved in the window — that way a
 * brand-new install doesn't show an empty card. We deliberately render
 * NOTHING (no skeleton, no placeholder) so the home/library doesn't
 * shift layout while the API call is in flight.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Sparkles, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/hooks/useFetch';

export const MostLovedPhraseCard = ({ isSpanish = false }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const { data: res } = await apiFetch('/api/phrases/most-loved-week?limit=1', {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (res?.phrases?.length) setData(res.phrases[0]);
      setLoaded(true);
    })();
    return () => controller.abort();
  }, []);

  if (!loaded || !data?.phrase) return null;

  const { phrase, save_count } = data;
  const phraseText = phrase.phrase || phrase.literalMeaning || '';
  const meaning = phrase.actualMeaning || phrase.literalMeaning || '';

  return (
    <div className="container max-w-2xl mx-auto px-4 my-4">
      <Card
        className="relative overflow-hidden border-rose-200 dark:border-rose-900 bg-gradient-to-br from-rose-50 via-card to-transparent dark:from-rose-950/30"
        data-testid="most-loved-phrase-card"
      >
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-rose-600 dark:text-rose-400">
            <Sparkles className="h-3.5 w-3.5" />
            {isSpanish ? 'Frase más amada esta semana' : 'Most-loved phrase this week'}
            <span className="ml-auto inline-flex items-center gap-1 normal-case text-muted-foreground font-normal">
              <Heart className="h-3 w-3 fill-rose-500 text-rose-500" />
              {save_count} {isSpanish ? (save_count === 1 ? 'guardado' : 'guardados') : (save_count === 1 ? 'save' : 'saves')}
            </span>
          </div>

          <h3
            className="text-lg sm:text-xl font-bold mb-1.5 line-clamp-2"
            data-testid="most-loved-phrase-text"
          >
            {phraseText}
          </h3>
          {meaning && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {meaning}
            </p>
          )}

          <Button
            variant="outline"
            size="sm"
            className="border-rose-300 hover:border-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30"
            onClick={() => navigate(`/phrase/${phrase.id}`)}
            data-testid="most-loved-phrase-cta"
          >
            {isSpanish ? 'Ver detalles' : 'See details'}
            <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default MostLovedPhraseCard;
